// Mock data for Timetable Rules and Preferences page

export interface TimetableRule {
  id: string;
  name: string;
  description: string;
  category: 'subject' | 'teacher' | 'student' | 'room' | 'time';
  type: 'hard' | 'soft' | 'preference' | 'teaching-preference';
  priority: 1 | 2 | 3 | 4 | 5;
  isActive: boolean;
  conditions: RuleCondition[];
  actions: RuleAction[];
  exceptions: RuleException[];
  createdBy: string;
  lastModified: string;
}

export interface RuleCondition {
  field: string;
  operator: 'equals' | 'not-equals' | 'contains' | 'in' | 'between' | 'greater' | 'less';
  value: any;
  logicalOperator?: 'AND' | 'OR';
}

export interface RuleAction {
  type: 'assign' | 'avoid' | 'prefer' | 'require' | 'restrict';
  target: string;
  value: any;
  weight?: number;
}

export interface RuleException {
  id: string;
  description: string;
  conditions: RuleCondition[];
  overrideAction: RuleAction;
  temporaryUntil?: string;
}

export interface SubjectConstraints {
  id: string;
  subjectId: string;
  subjectName: string;
  hardRules: {
    requiredRoomType?: string[];
    minimumPeriodsPerWeek: number;
    maximumPeriodsPerWeek: number;
    doublePeriodsRequired?: boolean;
    forbiddenTimeslots?: string[];
    requiredDayPattern?: string[];
  };
  softRules: {
    preferredTimeslots?: string[];
    preferredRooms?: string[];
    avoidConsecutiveDays?: boolean;
    morningPreference?: boolean;
    afternoonPreference?: boolean;
  };
}

export interface TeacherConstraints {
  id: string;
  teacherId: string;
  teacherName: string;
  hardRules: {
    unavailableDays: string[];
    unavailableTimeslots: string[];
    maxPeriodsPerDay: number;
    maxConsecutivePeriods: number;
    requiredBreakTime: number;
    contractualHours: number;
  };
  softRules: {
    preferredDays?: string[];
    preferredTimeslots?: string[];
    avoidEarlyMorning?: boolean;
    avoidLatePeriods?: boolean;
    preferredSubjects?: string[];
    workloadDistribution: 'even' | 'front-loaded' | 'back-loaded';
  };
  teachingPreferences: {
    yearGroupPreferences: string[];
    roomPreferences: string[];
    coTeachingWillingness: boolean;
    mentorshipAvailability: boolean;
    freePeriodDistribution: 'clustered' | 'spread' | 'no-preference';
  };
}

export interface PreferenceWeight {
  id: string;
  name: string;
  category: string;
  currentWeight: number;
  defaultWeight: number;
  range: { min: number; max: number };
  description: string;
  impactAnalysis: {
    affectedRules: string[];
    potentialConflicts: string[];
    recommendedValue: number;
  };
}

export interface WeightingProfile {
  id: string;
  name: string;
  description: string;
  weights: PreferenceWeight[];
  isDefault: boolean;
  lastUsed: string;
  successRate: number;
  averageConflicts: number;
}

export interface RuleConflict {
  id: string;
  type: 'hard-hard' | 'hard-soft' | 'soft-soft';
  severity: 'critical' | 'major' | 'minor';
  conflictingRules: string[];
  affectedEntities: {
    teachers?: string[];
    subjects?: string[];
    rooms?: string[];
    timeslots?: string[];
  };
  description: string;
  resolutionOptions: ResolutionOption[];
  suggestedResolution: string;
  status: 'unresolved' | 'resolved' | 'deferred';
}

export interface ResolutionOption {
  id: string;
  description: string;
  impact: string;
  confidence: number;
  action: 'modify-rule' | 'add-exception' | 'change-priority' | 'disable-rule';
}

export const mockData = {
  rules: [
    {
      id: 'R001',
      name: 'No Science Lab Double Booking',
      description: 'Ensure no two science classes are scheduled in the same laboratory simultaneously',
      category: 'room' as const,
      type: 'hard' as const,
      priority: 5,
      isActive: true,
      conditions: [
        {
          field: 'roomType',
          operator: 'equals' as const,
          value: 'laboratory',
        },
        {
          field: 'timeslot',
          operator: 'equals' as const,
          value: 'same',
          logicalOperator: 'AND' as const,
        },
      ],
      actions: [
        {
          type: 'restrict' as const,
          target: 'room_assignment',
          value: 'no_overlap',
        },
      ],
      exceptions: [],
      createdBy: 'System Administrator',
      lastModified: '2024-09-15',
    },
    {
      id: 'R002',
      name: 'Mathematics Morning Preference',
      description: 'Mathematics classes should preferably be scheduled in morning periods for better student focus',
      category: 'subject' as const,
      type: 'soft' as const,
      priority: 3,
      isActive: true,
      conditions: [
        {
          field: 'subject',
          operator: 'equals' as const,
          value: 'Mathematics',
        },
      ],
      actions: [
        {
          type: 'prefer' as const,
          target: 'timeslot',
          value: 'morning',
          weight: 0.7,
        },
      ],
      exceptions: [
        {
          id: 'E001',
          description: 'Year 11 can have afternoon math for exam preparation',
          conditions: [
            {
              field: 'yearGroup',
              operator: 'equals' as const,
              value: 'Year 11',
            },
          ],
          overrideAction: {
            type: 'prefer' as const,
            target: 'timeslot',
            value: 'afternoon',
            weight: 0.5,
          },
        },
      ],
      createdBy: 'Head of Mathematics',
      lastModified: '2024-09-10',
    },
    {
      id: 'R003',
      name: 'Teacher Lunch Break Protection',
      description: 'All teachers must have at least 30 minutes lunch break',
      category: 'teacher' as const,
      type: 'hard' as const,
      priority: 5,
      isActive: true,
      conditions: [
        {
          field: 'period',
          operator: 'equals' as const,
          value: 'lunch_period',
        },
      ],
      actions: [
        {
          type: 'require' as const,
          target: 'break_time',
          value: 30,
        },
      ],
      exceptions: [],
      createdBy: 'HR Department',
      lastModified: '2024-09-12',
    },
    {
      id: 'R004',
      name: 'PE Equipment Room Proximity',
      description: 'PE classes should be assigned to rooms near equipment storage',
      category: 'subject' as const,
      type: 'preference' as const,
      priority: 2,
      isActive: true,
      conditions: [
        {
          field: 'subject',
          operator: 'equals' as const,
          value: 'Physical Education',
        },
      ],
      actions: [
        {
          type: 'prefer' as const,
          target: 'room_proximity',
          value: 'equipment_storage',
          weight: 0.6,
        },
      ],
      exceptions: [],
      createdBy: 'PE Department',
      lastModified: '2024-09-08',
    },
    {
      id: 'R005',
      name: 'Senior Students Afternoon Preference',
      description: 'Year 10 and 11 students prefer afternoon slots for better attendance',
      category: 'student' as const,
      type: 'preference' as const,
      priority: 2,
      isActive: true,
      conditions: [
        {
          field: 'yearGroup',
          operator: 'in' as const,
          value: ['Year 10', 'Year 11'],
        },
      ],
      actions: [
        {
          type: 'prefer' as const,
          target: 'timeslot',
          value: 'afternoon',
          weight: 0.4,
        },
      ],
      exceptions: [],
      createdBy: 'Senior Leadership Team',
      lastModified: '2024-09-14',
    },
  ] as TimetableRule[],

  subjectConstraints: [
    {
      id: 'SC001',
      subjectId: 'CHEM',
      subjectName: 'Chemistry',
      hardRules: {
        requiredRoomType: ['laboratory'],
        minimumPeriodsPerWeek: 3,
        maximumPeriodsPerWeek: 5,
        doublePeriodsRequired: true,
        forbiddenTimeslots: ['Period 1'],
        requiredDayPattern: ['Monday', 'Wednesday', 'Friday'],
      },
      softRules: {
        preferredTimeslots: ['Period 2', 'Period 3'],
        preferredRooms: ['SL01', 'SL02'],
        avoidConsecutiveDays: false,
        morningPreference: true,
        afternoonPreference: false,
      },
    },
    {
      id: 'SC002',
      subjectId: 'MATH',
      subjectName: 'Mathematics',
      hardRules: {
        minimumPeriodsPerWeek: 4,
        maximumPeriodsPerWeek: 6,
        doublePeriodsRequired: false,
      },
      softRules: {
        preferredTimeslots: ['Period 1', 'Period 2', 'Period 3'],
        morningPreference: true,
        afternoonPreference: false,
        avoidConsecutiveDays: false,
      },
    },
    {
      id: 'SC003',
      subjectId: 'PE',
      subjectName: 'Physical Education',
      hardRules: {
        requiredRoomType: ['gymnasium', 'sports-hall'],
        minimumPeriodsPerWeek: 2,
        maximumPeriodsPerWeek: 3,
        doublePeriodsRequired: true,
      },
      softRules: {
        preferredTimeslots: ['Period 4', 'Period 5'],
        afternoonPreference: true,
        morningPreference: false,
      },
    },
  ] as SubjectConstraints[],

  teacherConstraints: [
    {
      id: 'TC001',
      teacherId: 'T001',
      teacherName: 'Sarah Johnson',
      hardRules: {
        unavailableDays: [],
        unavailableTimeslots: ['Period 6'],
        maxPeriodsPerDay: 5,
        maxConsecutivePeriods: 3,
        requiredBreakTime: 30,
        contractualHours: 22,
      },
      softRules: {
        preferredDays: ['Monday', 'Tuesday', 'Wednesday'],
        preferredTimeslots: ['Period 1', 'Period 2', 'Period 3'],
        avoidEarlyMorning: false,
        avoidLatePeriods: true,
        preferredSubjects: ['Mathematics', 'Statistics'],
        workloadDistribution: 'even' as const,
      },
      teachingPreferences: {
        yearGroupPreferences: ['Year 10', 'Year 11'],
        roomPreferences: ['M01', 'M02'],
        coTeachingWillingness: true,
        mentorshipAvailability: true,
        freePeriodDistribution: 'spread' as const,
      },
    },
    {
      id: 'TC002',
      teacherId: 'T002',
      teacherName: 'James Wilson',
      hardRules: {
        unavailableDays: ['Friday'],
        unavailableTimeslots: ['Period 5', 'Period 6'],
        maxPeriodsPerDay: 4,
        maxConsecutivePeriods: 2,
        requiredBreakTime: 45,
        contractualHours: 16,
      },
      softRules: {
        preferredDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
        preferredTimeslots: ['Period 1', 'Period 2'],
        avoidEarlyMorning: false,
        avoidLatePeriods: true,
        preferredSubjects: ['English Literature'],
        workloadDistribution: 'front-loaded' as const,
      },
      teachingPreferences: {
        yearGroupPreferences: ['Year 9', 'Year 10'],
        roomPreferences: ['E01'],
        coTeachingWillingness: false,
        mentorshipAvailability: true,
        freePeriodDistribution: 'clustered' as const,
      },
    },
  ] as TeacherConstraints[],

  preferenceWeights: [
    {
      id: 'PW001',
      name: 'Teacher Workload Balance',
      category: 'teacher',
      currentWeight: 0.8,
      defaultWeight: 0.7,
      range: { min: 0.1, max: 1.0 },
      description: 'Weight for balancing workload across teachers',
      impactAnalysis: {
        affectedRules: ['R003', 'TC001', 'TC002'],
        potentialConflicts: ['Subject requirements vs teacher availability'],
        recommendedValue: 0.75,
      },
    },
    {
      id: 'PW002',
      name: 'Room Utilization Efficiency',
      category: 'room',
      currentWeight: 0.6,
      defaultWeight: 0.5,
      range: { min: 0.1, max: 1.0 },
      description: 'Weight for optimizing room usage',
      impactAnalysis: {
        affectedRules: ['R001', 'R004'],
        potentialConflicts: ['Room preferences vs availability'],
        recommendedValue: 0.6,
      },
    },
    {
      id: 'PW003',
      name: 'Student Learning Optimization',
      category: 'student',
      currentWeight: 0.9,
      defaultWeight: 0.8,
      range: { min: 0.1, max: 1.0 },
      description: 'Weight for optimizing student learning conditions',
      impactAnalysis: {
        affectedRules: ['R002', 'R005'],
        potentialConflicts: ['Learning optimization vs resource constraints'],
        recommendedValue: 0.85,
      },
    },
    {
      id: 'PW004',
      name: 'Subject Continuity',
      category: 'subject',
      currentWeight: 0.7,
      defaultWeight: 0.6,
      range: { min: 0.1, max: 1.0 },
      description: 'Weight for maintaining subject scheduling consistency',
      impactAnalysis: {
        affectedRules: ['SC001', 'SC002', 'SC003'],
        potentialConflicts: ['Subject requirements vs teacher preferences'],
        recommendedValue: 0.65,
      },
    },
  ] as PreferenceWeight[],

  weightingProfiles: [
    {
      id: 'WP001',
      name: 'Balanced Approach',
      description: 'Equal consideration for all factors',
      weights: [],
      isDefault: true,
      lastUsed: '2024-09-15',
      successRate: 85,
      averageConflicts: 3.2,
    },
    {
      id: 'WP002',
      name: 'Teacher-Focused',
      description: 'Prioritize teacher preferences and workload',
      weights: [],
      isDefault: false,
      lastUsed: '2024-09-10',
      successRate: 78,
      averageConflicts: 4.1,
    },
    {
      id: 'WP003',
      name: 'Student-Centered',
      description: 'Maximize student learning opportunities',
      weights: [],
      isDefault: false,
      lastUsed: '2024-09-12',
      successRate: 82,
      averageConflicts: 3.8,
    },
  ] as WeightingProfile[],

  conflicts: [
    {
      id: 'C001',
      type: 'hard-hard' as const,
      severity: 'critical' as const,
      conflictingRules: ['R001', 'SC001'],
      affectedEntities: {
        subjects: ['Chemistry'],
        rooms: ['SL01'],
        timeslots: ['Period 2 Monday'],
      },
      description: 'Chemistry double period conflicts with laboratory availability',
      resolutionOptions: [
        {
          id: 'RO001',
          description: 'Reassign chemistry to alternative lab',
          impact: 'Minimal impact on learning quality',
          confidence: 0.9,
          action: 'modify-rule' as const,
        },
        {
          id: 'RO002',
          description: 'Split chemistry into single periods',
          impact: 'Reduced practical session effectiveness',
          confidence: 0.6,
          action: 'modify-rule' as const,
        },
      ],
      suggestedResolution: 'RO001',
      status: 'unresolved' as const,
    },
    {
      id: 'C002',
      type: 'soft-soft' as const,
      severity: 'minor' as const,
      conflictingRules: ['R002', 'TC002'],
      affectedEntities: {
        teachers: ['James Wilson'],
        subjects: ['Mathematics'],
        timeslots: ['Period 5'],
      },
      description: 'Math morning preference conflicts with teacher afternoon availability',
      resolutionOptions: [
        {
          id: 'RO003',
          description: 'Reduce weight of morning preference for this teacher',
          impact: 'Slight reduction in optimal scheduling',
          confidence: 0.8,
          action: 'change-priority' as const,
        },
        {
          id: 'RO004',
          description: 'Add exception for part-time teachers',
          impact: 'Maintains rule integrity while accommodating constraints',
          confidence: 0.9,
          action: 'add-exception' as const,
        },
      ],
      suggestedResolution: 'RO004',
      status: 'resolved' as const,
    },
  ] as RuleConflict[],
};
